import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { formatNumberWithCommas } from 'utils/formats';
import { toastConfig } from 'utils/toastConfig';
import { TPurchaseOrderItem } from 'utils/types';

type EstimatedUnitPriceCellProps = {
  item: TPurchaseOrderItem;
  branchDomain?: string;
  purchaseOrderId?: string;
  canEdit: boolean;
  onUpdated: (item: TPurchaseOrderItem) => void;
};

const formatPeso = (value: number | null | undefined) =>
  value == null ? '—' : `₱${formatNumberWithCommas(value)}`;

const EstimatedUnitPriceCell = ({
  item,
  branchDomain,
  purchaseOrderId,
  canEdit,
  onUpdated
}: EstimatedUnitPriceCellProps) => {
  const [value, setValue] = useState(
    item.estimatedUnitPrice != null ? String(item.estimatedUnitPrice) : ''
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(
      item.estimatedUnitPrice != null ? String(item.estimatedUnitPrice) : ''
    );
  }, [item.estimatedUnitPrice]);

  if (item.productId) {
    return <span>{formatPeso(item.estimatedUnitPrice)}</span>;
  }

  if (!canEdit) {
    return <span>{formatPeso(item.estimatedUnitPrice)}</span>;
  }

  const save = async () => {
    if (!branchDomain || !purchaseOrderId) {
      return;
    }

    const parsed = value.trim() === '' ? null : Number(value);
    if (parsed != null && (Number.isNaN(parsed) || parsed < 0)) {
      toast.error('Enter a valid estimated price', {
        ...toastConfig,
        autoClose: 3000
      });
      setValue(
        item.estimatedUnitPrice != null ? String(item.estimatedUnitPrice) : ''
      );
      return;
    }

    const current = item.estimatedUnitPrice ?? null;
    if (parsed === current) {
      return;
    }

    setIsSaving(true);
    try {
      const res = await axiosInstance.put<TPurchaseOrderItem>(
        `/api/v1/${branchDomain}/purchaseOrders/${purchaseOrderId}/items/${item.purchaseOrderItemId}/estimatedPrice`,
        { estimatedUnitPrice: parsed }
      );
      onUpdated({
        ...item,
        ...res.data,
        estimatedUnitPrice: res.data.estimatedUnitPrice ?? parsed,
        estimatedAmount:
          res.data.estimatedAmount ??
          (parsed != null ? parsed * item.quantity : null)
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to save estimated price',
          { ...toastConfig, autoClose: 3000 }
        );
      }
      setValue(
        item.estimatedUnitPrice != null ? String(item.estimatedUnitPrice) : ''
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form.Control
      type="number"
      min={0}
      step="0.01"
      size="sm"
      value={value}
      disabled={isSaving}
      placeholder="0.00"
      onChange={e => setValue(e.target.value)}
      onBlur={() => void save()}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      style={{ maxWidth: 110 }}
    />
  );
};

export default EstimatedUnitPriceCell;
export { formatPeso };
